import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Plus, X, Mail } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface InviteToAppDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const InviteToAppDialog = ({
  open,
  onOpenChange,
}: InviteToAppDialogProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [emails, setEmails] = useState<string[]>([]);
  const [currentEmail, setCurrentEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleAddEmail = () => {
    const trimmedEmail = currentEmail.trim().toLowerCase();
    
    if (!trimmedEmail) {
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      toast({
        title: t("inviteApp.toastInvalidEmail"),
        description: t("inviteApp.toastInvalidEmailDesc"),
        variant: "destructive",
      });
      return;
    }

    if (emails.includes(trimmedEmail)) {
      toast({
        title: t("inviteApp.toastEmailExists"),
        description: t("inviteApp.toastEmailExistsDesc"),
        variant: "destructive",
      });
      return;
    }

    setEmails([...emails, trimmedEmail]);
    setCurrentEmail("");
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    setEmails(emails.filter(email => email !== emailToRemove));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleInvite = async () => {
    if (emails.length === 0) {
      toast({
        title: t("inviteApp.toastNoEmail"),
        description: t("inviteApp.toastNoEmailDesc"),
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: t("inviteApp.toastNotAuth"),
          description: t("inviteApp.toastNotAuthDesc"),
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      // Insert app invitations
      const { error } = await supabase
        .from("app_invitations")
        .insert(
          emails.map(email => ({
            email: email,
            invited_by: user.id,
          }))
        );

      if (error) {
        if (error.code === "23505") {
          toast({
            title: t("inviteApp.toastDuplicate"),
            description: t("inviteApp.toastDuplicateDesc"),
            variant: "destructive",
          });
        } else {
          throw error;
        }
        setLoading(false);
        return;
      }

      // Send invitation emails in background
      const inviterName = user.user_metadata?.full_name || user.email?.split('@')[0] || "Un utilisateur";
      
      supabase.functions.invoke("send-app-invitation", {
        body: {
          emails,
          inviterName,
        },
      }).catch(err => {
        console.error("Error sending invitation emails:", err);
      });

      toast({
        title: t("inviteApp.toastSuccess"),
        description: t("inviteApp.toastSuccessDesc", { count: emails.length }),
      });

      setEmails([]);
      setCurrentEmail("");
      onOpenChange(false);
    } catch (error) {
      console.error("Error sending invitations:", error);
      toast({
        title: t("inviteApp.toastError"),
        description: t("inviteApp.toastErrorDesc"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t("inviteApp.title")}</DialogTitle>
          <DialogDescription>
            {t("inviteApp.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="email">{t("inviteApp.emailLabel")}</Label>
            <div className="flex gap-2">
              <Input
                id="email"
                type="email"
                placeholder={t("inviteApp.emailPlaceholder")}
                value={currentEmail}
                onChange={(e) => setCurrentEmail(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <Button type="button" onClick={handleAddEmail} size="icon">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {emails.length > 0 && (
            <div className="space-y-2">
              <Label>{t("inviteApp.peopleToInvite")} ({emails.length})</Label>
              <div className="flex flex-wrap gap-2 p-3 bg-muted rounded-md max-h-32 overflow-y-auto">
                {emails.map((email) => (
                  <Badge key={email} variant="secondary" className="gap-2">
                    <Mail className="h-3 w-3" />
                    {email}
                    <button
                      type="button"
                      onClick={() => handleRemoveEmail(email)}
                      className="ml-1 hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              {t("inviteApp.cancel")}
            </Button>
            <Button
              type="button"
              onClick={handleInvite}
              disabled={loading || emails.length === 0}
              className="flex-1"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("inviteApp.invite")} {emails.length > 0 && `(${emails.length})`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InviteToAppDialog;
